import { useCallback, useMemo, useState } from "react";
import type { InputActionMeta } from "react-select";
import { Button } from "@/shared/components/form/Button";
import { InputSelect } from "@/shared/components/form/InputSelect";
import type { PaymentAuditFiltersState } from "../types/auditoria.types";
import type { ClientSummary, OrgWithClients, ClientWithOrg } from "../types/auditoria-api.types";
import useTranslations from "@/shared/lang/useTranslations";
import { CURRENCY_OPTIONS } from "../../confirmations/constants/currencies.config";
import { PAYMENT_SOURCE_OPTIONS } from "../../payments/constants/paymentSourceOptions";
import { useAuditClientsSearch } from "../hooks/useAuditClientsSearch";

type SelectOption = { label: string; value: string };

function orgDisplayName(o: OrgWithClients): string {
  return o.name?.trim() || o.id;
}

function clientWithOrgDisplayName(c: ClientWithOrg): string {
  if (c.company_name?.trim()) return c.company_name.trim();
  const parts = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return parts || c.email || c.id;
}

function clientSummaryDisplayName(c: ClientSummary): string {
  if (c.company_name?.trim()) return c.company_name.trim();
  const parts = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return parts || c.id;
}

interface PaymentAuditFiltersProps {
  filters: PaymentAuditFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<PaymentAuditFiltersState>>;
  onApply: () => void;
  onReset: () => void;
  isLoading?: boolean;
}

export const PaymentAuditFilters = ({
  filters,
  setFilters,
  onApply,
  onReset,
  isLoading = false,
}: PaymentAuditFiltersProps) => {
  const { t } = useTranslations("audit");

  const [orgMenuInput, setOrgMenuInput] = useState("");
  const [clientMenuInput, setClientMenuInput] = useState("");
  const [selectedOrgOption, setSelectedOrgOption] = useState<SelectOption | null>(null);
  const [selectedClientOption, setSelectedClientOption] = useState<SelectOption | null>(null);
  const [selectedOrgClients, setSelectedOrgClients] = useState<ClientSummary[]>([]);

  const { organizations, clients, isOrgLoading, isClientLoading } = useAuditClientsSearch(
    orgMenuInput,
    clientMenuInput
  );

  const orgOptions: SelectOption[] = useMemo(() => {
    const fromSearch = organizations.map((o) => ({ value: o.id, label: orgDisplayName(o) }));
    if (selectedOrgOption && !fromSearch.some((o) => o.value === selectedOrgOption.value)) {
      return [selectedOrgOption, ...fromSearch];
    }
    return fromSearch;
  }, [organizations, selectedOrgOption]);

  const clientOptions: SelectOption[] = useMemo(() => {
    if (clientMenuInput.trim()) {
      const fromSearch = clients.map((c) => ({
        value: c.id,
        label: clientWithOrgDisplayName(c),
      }));
      if (selectedClientOption && !fromSearch.some((o) => o.value === selectedClientOption.value)) {
        return [selectedClientOption, ...fromSearch];
      }
      return fromSearch;
    }
    if (selectedOrgClients.length > 0) {
      const fromOrg = selectedOrgClients.map((c) => ({
        value: c.id,
        label: clientSummaryDisplayName(c),
      }));
      if (selectedClientOption && !fromOrg.some((o) => o.value === selectedClientOption.value)) {
        return [selectedClientOption, ...fromOrg];
      }
      return fromOrg;
    }
    if (selectedClientOption) return [selectedClientOption];
    return [];
  }, [clients, clientMenuInput, selectedOrgClients, selectedClientOption]);

  const currencyOptions: SelectOption[] = useMemo(
    () => CURRENCY_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
    []
  );

  const paymentSourceOptions: SelectOption[] = useMemo(
    () => PAYMENT_SOURCE_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
    []
  );

  const currencyValue = filters.currency.trim()
    ? currencyOptions.find((o) => o.value === filters.currency) ?? null
    : null;

  const paymentSourceValue = filters.payment_source.trim()
    ? paymentSourceOptions.find((o) => o.value === filters.payment_source) ?? null
    : null;

  const handleOrgInputChange = useCallback((input: string, meta: InputActionMeta) => {
    if (meta.action === "input-change") {
      setOrgMenuInput(input);
    }
  }, []);

  const handleClientInputChange = useCallback((input: string, meta: InputActionMeta) => {
    if (meta.action === "input-change") {
      setClientMenuInput(input);
    }
  }, []);

  const handleOrgChange = useCallback(
    (selected: SelectOption | SelectOption[] | null) => {
      const opt = Array.isArray(selected) ? selected[0] : selected;
      setOrgMenuInput("");
      if (!opt) {
        setSelectedOrgOption(null);
        setSelectedOrgClients([]);
        setSelectedClientOption(null);
        setFilters((prev) => ({ ...prev, organization_name: "", client_name: "" }));
        return;
      }
      const org = organizations.find((o) => o.id === opt.value);
      setSelectedOrgOption(opt);
      setSelectedOrgClients(org?.clients ?? []);
      setSelectedClientOption(null);
      setFilters((prev) => ({
        ...prev,
        organization_name: org?.name ?? opt.label,
        client_name: "",
      }));
    },
    [organizations, setFilters]
  );

  const handleClientChange = useCallback(
    (selected: SelectOption | SelectOption[] | null) => {
      const opt = Array.isArray(selected) ? selected[0] : selected;
      setClientMenuInput("");
      if (!opt) {
        setSelectedClientOption(null);
        setFilters((prev) => ({ ...prev, client_name: "" }));
        return;
      }
      setSelectedClientOption(opt);
      const clientFromSearch = clients.find((c) => c.id === opt.value);
      if (clientFromSearch) {
        const orgName = clientFromSearch.organization?.name?.trim() ?? "";
        const orgId = clientFromSearch.organization?.id ?? "";
        const orgOpt: SelectOption | null = orgName ? { value: orgId, label: orgName } : null;
        setSelectedOrgOption(orgOpt);
        setSelectedOrgClients([]);
        setFilters((prev) => ({
          ...prev,
          client_name: opt.label,
          organization_name: orgName,
        }));
      } else {
        setFilters((prev) => ({ ...prev, client_name: opt.label }));
      }
    },
    [clients, setFilters]
  );

  const handleReset = useCallback(() => {
    setOrgMenuInput("");
    setClientMenuInput("");
    setSelectedOrgOption(null);
    setSelectedClientOption(null);
    setSelectedOrgClients([]);
    onReset();
  }, [onReset]);

  return (
    <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t("payments_filters_heading")}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <InputSelect<SelectOption>
            name="payment-audit-org-name"
            label={t("payments_filters_label_org_name")}
            options={orgOptions}
            value={selectedOrgOption}
            onChange={(v) => handleOrgChange((Array.isArray(v) ? v[0] : v) ?? null)}
            onInputChange={handleOrgInputChange}
            filterOption={() => true}
            placeholder={t("payments_filters_placeholder_org_name")}
            isLoading={isOrgLoading}
            isClearable
            maxWidth="max-w-full"
            controlClassName="!h-11"
            noOptionsMessage={({ inputValue }) =>
              inputValue.trim() ? t("filters_dropdown_empty") : t("payments_filters_type_to_search")
            }
          />
        </div>
        <div>
          <InputSelect<SelectOption>
            name="payment-audit-client-name"
            label={t("payments_filters_label_client_name")}
            options={clientOptions}
            value={selectedClientOption}
            onChange={(v) => handleClientChange((Array.isArray(v) ? v[0] : v) ?? null)}
            onInputChange={handleClientInputChange}
            filterOption={() => true}
            placeholder={t("payments_filters_placeholder_client_name")}
            isLoading={isClientLoading}
            isClearable
            maxWidth="max-w-full"
            controlClassName="!h-11"
            noOptionsMessage={({ inputValue }) =>
              inputValue.trim() || selectedOrgClients.length > 0
                ? t("filters_dropdown_empty")
                : t("payments_filters_type_to_search")
            }
          />
        </div>
        <div>
          <InputSelect<SelectOption>
            name="payment-audit-currency"
            label={t("table_th_currency")}
            options={currencyOptions}
            value={currencyValue}
            onChange={(selected) => {
              const v = Array.isArray(selected) ? selected[0]?.value : selected?.value;
              setFilters((prev) => ({
                ...prev,
                currency: typeof v === "string" ? v : "",
              }));
            }}
            placeholder={t("filters_placeholder_flujo")}
            isClearable
            maxWidth="max-w-full"
            controlClassName="!h-11"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("filters_label_date_start")}
          </label>
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, start_date: e.target.value }))
            }
            className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("filters_label_date_end")}
          </label>
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, end_date: e.target.value }))
            }
            className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <div>
          <InputSelect<SelectOption>
            name="payment-audit-payment-source"
            label={t("payments_filters_label_payment_source")}
            options={paymentSourceOptions}
            value={paymentSourceValue}
            onChange={(selected) => {
              const v = Array.isArray(selected) ? selected[0]?.value : selected?.value;
              setFilters((prev) => ({
                ...prev,
                payment_source: typeof v === "string" ? v : "",
              }));
            }}
            placeholder={t("payments_filters_placeholder_payment_source")}
            isClearable
            maxWidth="max-w-full"
            controlClassName="!h-11"
          />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t("payments_filters_hint")}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("filters_label_amount_min")}
          </label>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={filters.amount_min}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, amount_min: e.target.value }))
            }
            placeholder={t("filters_placeholder_amount_min")}
            className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("filters_label_amount_max")}
          </label>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={filters.amount_max}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, amount_max: e.target.value }))
            }
            placeholder={t("filters_placeholder_amount_max")}
            className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      <div className="flex gap-3 mt-4">
        <Button type="button" variant="primary" onClick={onApply} disabled={isLoading}>
          {t("filters_button_apply")}
        </Button>
        <Button type="button" variant="outline" onClick={handleReset} disabled={isLoading}>
          {t("filters_button_clear")}
        </Button>
      </div>
    </div>
  );
};
